import { PaginatedFieldsContext } from '@cibo/landmanager'
import HandshakeRounded from '@mui/icons-material/HandshakeRounded'
import ThumbDownAltRounded from '@mui/icons-material/ThumbDownAltRounded'
import ThumbsUpDownRounded from '@mui/icons-material/ThumbsUpDownRounded'
import { Button, Paper, Stack, Typography } from '@mui/material'
import { useSnackbar } from 'notistack'
import { Dispatch, SetStateAction, useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { useAgreement, useUpdateAgreement } from '../queries'
import { AgreementStatus } from '../types'
import { DeclineDialogState } from './OfferDeclineDialog'

const buttonIcon = (declineCount: number, selectedRowIds: any[]) =>
  declineCount > 0 && selectedRowIds.length > 0 ? (
    <ThumbsUpDownRounded />
  ) : !declineCount ? (
    <HandshakeRounded />
  ) : (
    <ThumbDownAltRounded />
  )

export const OfferAcceptButton = ({
  numItems,
  setDeclineDialog,
  setAcceptInfoDialogOpen,
  agreementId,
}: {
  numItems?: number
  setDeclineDialog: Dispatch<SetStateAction<DeclineDialogState>>
  setAcceptInfoDialogOpen: Dispatch<SetStateAction<boolean>>
  agreementId: string
}) => {
  const { t } = useTranslation(['impact/pages/Agreements/OfferDetail', 'impact/pages/Agreements'])
  const agreement = useAgreement(agreementId)
  const { selectedRowIds, selectedFields } = useContext(PaginatedFieldsContext)
  const acceptAmount = selectedFields.reduce((acc, field) => (field.offerPayment() ?? 0) + acc, 0)
  const { enqueueSnackbar } = useSnackbar()
  const { mutateAsync: updateAgreement } = useUpdateAgreement()
  const declineCount = numItems ? numItems - selectedRowIds.length : 0

  const onComplete = (status: AgreementStatus) => {
    if (declineCount > 0 || status !== 'agreed') {
      setDeclineDialog({ open: true, action: status })
    } else {
      updateAgreement({ agreementId, agreedIds: selectedRowIds as string[], status })
        .then(() => {
          setAcceptInfoDialogOpen(true)
        })
        .catch(() => {
          enqueueSnackbar(t('failedTryAgain', { ns: 'impact/pages/Agreements' }))
        })
    }
  }

  return (
    <>
      <Typography variant="h5" color="text.secondary" textAlign={{ xs: 'left', md: 'right' }}>
        {t('pendingTitle')}
      </Typography>
      <Paper elevation={0}>
        <Stack padding={2}>
          <Stack spacing={2} textAlign={{ xs: 'left', md: 'right' }}>
            <Stack spacing={3}>
              <Typography
                color="text.secondary"
                sx={{
                  opacity: declineCount > 0 ? 1 : 0,
                  height: declineCount > 0 ? 'auto' : 0,
                  marginTop: declineCount > 0 ? undefined : '0 !important',
                }}
              >
                {t('declineAmount', { count: declineCount })}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  opacity: selectedRowIds.length > 0 ? 1 : 0,
                  height: selectedRowIds.length ? 'auto' : 0,
                  marginTop: selectedRowIds.length && declineCount > 0 ? undefined : '0 !important',
                }}
              >
                {t('acceptAmount', { count: selectedRowIds.length, acceptAmount })}
              </Typography>
            </Stack>
            <Button
              data-testid="submit-button"
              variant="contained"
              sx={{
                marginTop: !selectedRowIds.length && !declineCount ? '0 !important' : undefined,
              }}
              disabled={
                (!declineCount && !selectedRowIds.length) || agreement.data?.status !== 'pending'
              }
              onClick={() => onComplete(!selectedRowIds.length ? 'declined' : 'agreed')}
              endIcon={buttonIcon(declineCount, selectedRowIds)}
              fullWidth
            >
              {t('submitButton')}
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </>
  )
}
